// Aba "Geradas" da Planilha Gran: toda planilha baixada para a Gran fica guardada como
// foto (planilha_gran_envios). Reabrir baixa exatamente o que a Gran recebeu naquele dia,
// mesmo que a fatura tenha mudado depois.
import React, { useEffect, useState } from 'react';
import { Download, History, Loader2 } from 'lucide-react';
import { useToast } from '../../../src/contexts/ToastContext';
import * as api from './planilhaGranApi';
import { baixarPlanilhaParaGran } from './exportarPlanilhaGran';

interface Props {
  empresaId: string;
  /** Muda quando uma planilha nova é registrada — recarrega a lista. */
  versao: number;
}

const brl = (v: number) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dataHora = (iso: string) => new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const AbaEnviadasGran: React.FC<Props> = ({ empresaId, versao }) => {
  const { addToast } = useToast();
  const [envios, setEnvios] = useState<api.EnvioPlanilhaGran[] | null>(null);
  const [autores, setAutores] = useState<Record<string, string>>({});
  const [erro, setErro] = useState('');
  const [baixando, setBaixando] = useState<number | null>(null);

  useEffect(() => {
    let vivo = true;
    setErro('');
    api.listarEnvios(empresaId)
      .then(async lista => {
        if (!vivo) return;
        setEnvios(lista);
        const nomes = await api.nomesDosAutores(lista.map(e => e.gerado_por || ''));
        if (vivo) setAutores(nomes);
      })
      .catch(e => { if (vivo) { setErro(e.message); setEnvios([]); } });
    return () => { vivo = false; };
  }, [empresaId, versao]);

  const baixar = async (e: api.EnvioPlanilhaGran) => {
    setBaixando(e.id);
    try {
      baixarPlanilhaParaGran(await api.carregarLinhasDoEnvio(e.id), e.mes_referencia);
    } catch (err: any) {
      addToast(err.message, 'error');
    } finally {
      setBaixando(null);
    }
  };

  if (envios === null) {
    return <div className="h-full flex items-center justify-center text-sm text-gray-500"><Loader2 className="w-5 h-5 animate-spin mr-2" aria-hidden="true" /> Carregando histórico…</div>;
  }
  if (erro) {
    return <div className="h-full flex items-center justify-center p-8 text-center text-sm text-red-600 dark:text-red-400">{erro}</div>;
  }
  if (envios.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-2 p-8 text-center">
        <History className="w-8 h-8 text-gray-300 dark:text-gray-600" aria-hidden="true" />
        <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">Nenhuma planilha gerada ainda</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 max-w-md">Cada vez que o Financeiro baixa a planilha "para a Gran", uma cópia fica guardada aqui para reabrir depois.</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-3">
      <table className="w-full text-[12px]">
        <thead className="text-left text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
          <tr className="border-b border-gray-200 dark:border-gray-700">
            <th scope="col" className="py-2 px-2 font-semibold">Gerada em</th>
            <th scope="col" className="py-2 px-2 font-semibold">Mês</th>
            <th scope="col" className="py-2 px-2 font-semibold text-right">UCs</th>
            <th scope="col" className="py-2 px-2 font-semibold text-right">Boleto Gran</th>
            <th scope="col" className="py-2 px-2 font-semibold text-right">Equatorial</th>
            <th scope="col" className="py-2 px-2 font-semibold text-right">Comissão</th>
            <th scope="col" className="py-2 px-2 font-semibold">Recorte</th>
            <th scope="col" className="py-2 px-2 font-semibold">Por</th>
            <th scope="col" className="py-2 px-2"><span className="sr-only">Ações</span></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
          {envios.map(e => (
            <tr key={e.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/40">
              <td className="py-2 px-2 whitespace-nowrap">{dataHora(e.gerado_em)}</td>
              <td className="py-2 px-2 font-semibold">{e.mes_referencia}</td>
              <td className="py-2 px-2 text-right tabular-nums">{e.qtd_linhas}</td>
              <td className="py-2 px-2 text-right tabular-nums">{brl(e.total_boleto)}</td>
              <td className="py-2 px-2 text-right tabular-nums">{brl(e.total_equatorial)}</td>
              <td className="py-2 px-2 text-right tabular-nums">{brl(e.total_comissao)}</td>
              <td className="py-2 px-2 text-gray-500 dark:text-gray-400">{e.filtro || 'Planilha inteira'}</td>
              <td className="py-2 px-2 text-gray-500 dark:text-gray-400">{(e.gerado_por && autores[e.gerado_por]) || '—'}</td>
              <td className="py-2 px-2 text-right">
                <button
                  type="button"
                  onClick={() => baixar(e)}
                  disabled={baixando === e.id}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-md border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
                >
                  {baixando === e.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> : <Download className="w-3.5 h-3.5" aria-hidden="true" />}
                  Baixar de novo
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AbaEnviadasGran;
